import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Eye, EyeOff, Lock, Mail, User, Shield, FileSignature } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import { API_BASE_URL } from '../config/api'
import '../styles/auth.css'

const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isAuthenticated, isLoading } = useAuth()
  
  // Obtener token de firma de la URL si existe
  const searchParams = new URLSearchParams(location.search)
  const tokenFirma = searchParams.get('token_firma')
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [isSendingReset, setIsSendingReset] = useState(false)
  const [resetMessage, setResetMessage] = useState({ type: '', text: '' })

  // Redirigir si ya está autenticado (solo una vez cuando deja de cargar y está autenticado)
  useEffect(() => {
    // Solo redirigir si NO está cargando Y está autenticado
    // Evitar ejecutar si isLoading cambia de true a false pero no está autenticado
    if (!isLoading) {
      const authenticated = isAuthenticated()
      if (authenticated) {
        // Si hay un token de firma, redirigir al editor para firmar
        if (tokenFirma) {
          navigate(`/editor/${tokenFirma}`, { replace: true })
        } else {
          navigate('/dashboard', { replace: true })
        }
      }
    }
  }, [isLoading, tokenFirma]) // Incluir tokenFirma en las dependencias

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const result = await login(formData.email, formData.password, rememberMe)
      // Si requiere cambio de contraseña, redirigir a /change-password
      if (result?.requires_password_change) {
        navigate('/change-password', { replace: true })
      } else {
        // Si hay un token de firma en la URL, redirigir al editor para firmar el documento
        if (tokenFirma) {
          toast.success('Inicio de sesión exitoso. Redirigiendo al documento para firmar...')
          navigate(`/editor/${tokenFirma}`, { replace: true })
        } else {
          // Redirigir al dashboard después del login exitoso
          navigate('/dashboard', { replace: true })
        }
      }
    } catch (error) {
      // El error ya se maneja en el hook useAuth
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDemoLogin = () => {
    setFormData({
      email: 'admin@induma.com.co',
      password: 'admin123'
    })
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setIsSendingReset(true)
    setResetMessage({ type: '', text: '' })

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: forgotPasswordEmail }),
      })

      const data = await response.json()

      if (response.ok && data.success === true) {
        setResetMessage({
          type: 'success',
          text:
            data.message ||
            'Si el correo está registrado, recibirás instrucciones para restablecer tu contraseña.',
        })
        setForgotPasswordEmail('')
        setTimeout(() => {
          setShowForgotPasswordModal(false)
          setResetMessage({ type: '', text: '' })
        }, 3000)
      } else {
        setResetMessage({
          type: 'error',
          text: data.detail || data.message || 'Error al enviar la solicitud. Inténtalo de nuevo.',
        })
      }
    } catch (error) {
      console.error('Error:', error)
      setResetMessage({
        type: 'error',
        text: 'Error de conexión. Inténtalo de nuevo.'
      })
    } finally {
      setIsSendingReset(false)
    }
  }

  return (
    <div className="auth-body">
      <div className="auth-container">
        <div className="auth-card">
          {/* Header */}
          <div className="auth-header">
            <div className="logo">
              <FileSignature size={48} />
              <h1>INDUMA</h1>
            </div>
            <p className="auth-subtitle">Sistema de Firma Electrónica</p>
            {tokenFirma && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 text-center">
                  <strong>📝 Documento pendiente de firma</strong><br />
                  Inicia sesión para acceder al documento y firmarlo
                </p>
              </div>
            )}
          </div>

          {/* Formulario de Login */}
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Correo Electrónico</label>
              <div className="input-group">
                <Mail size={20} />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="tu-email@induma.com.co"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Contraseña</label>
              <div className="input-group">
                <Lock size={20} />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Tu contraseña"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Recordar sesión</span>
              </label>
              <button 
                type="button" 
                className="forgot-password" 
                onClick={() => setShowForgotPasswordModal(true)}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            <button
              type="submit"
              className="auth-btn"
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Iniciando sesión...
                </>
              ) : (
                <>
                  <User size={18} />
                  Iniciar Sesión
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="auth-footer">
            <p>© 2025 INDUMA S.A.S - Todos los derechos reservados | create by "jpz"</p>
          </div>
        </div>
      </div>

      {/* Modal de Recuperación de Contraseña */}
      {showForgotPasswordModal && (
        <div className="modal-overlay" onClick={() => !isSendingReset && setShowForgotPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close" 
              onClick={() => setShowForgotPasswordModal(false)}
              disabled={isSendingReset}
            >
              ×
            </button>
            <h2 style={{ marginBottom: '1rem', color: '#C8202F' }}>🔑 Recuperar Contraseña</h2>
            <p style={{ marginBottom: '1.5rem', color: '#666' }}>
              Ingresa tu email para recibir una contraseña temporal:
            </p>
            
            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <label htmlFor="resetEmail">Email:</label>
                <div className="input-group">
                  <Mail size={20} />
                  <input
                    id="resetEmail"
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    required
                    placeholder="tu-email@induma.com.co"
                    disabled={isSendingReset}
                  />
                </div>
              </div>

              {resetMessage.text && (
                <div className={`reset-message ${resetMessage.type}`}>
                  {resetMessage.text}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="auth-btn-secondary"
                  onClick={() => setShowForgotPasswordModal(false)}
                  disabled={isSendingReset}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="auth-btn"
                  disabled={isSendingReset}
                >
                  {isSendingReset ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Enviando...
                    </>
                  ) : (
                    'Enviar Contraseña Temporal'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Login

